import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Search, Download, Heart, CheckCircle2, XCircle, Clock, TrendingUp } from "lucide-react";
import { api, useAuth } from "../../lib/auth";
import { useCapabilities } from "../../lib/useCapabilities";

const KNOWN_SOURCES = [
  "Direct", "QR Code", "Facebook", "Instagram", "LinkedIn",
  "Email", "Website", "Referral", "Event", "Other",
];

const statusPill = (s) => {
  if (s === "paid")
    return {
      cls: "bg-[#2D9B4E]/10 text-[#23803F] border-[#2D9B4E]/30",
      Icon: CheckCircle2,
      label: "PAID",
    };
  if (s === "unpaid")
    return {
      cls: "bg-gray-100 text-gray-600 border-gray-200",
      Icon: Clock,
      label: "UNPAID",
    };
  return { cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle, label: s.toUpperCase() };
};

const sourceColor = (s) => {
  const map = {
    Direct: "#4B5563",
    "QR Code": "#0B132B",
    Facebook: "#1877F2",
    Instagram: "#E1306C",
    LinkedIn: "#0A66C2",
    Email: "#1A5FAB",
    Website: "#1F5D5B",
    Referral: "#2D9B4E",
    Event: "#B45309",
    Other: "#6B7280",
  };
  return map[s] || "#6B7280";
};

export default function AdminDonations() {
  const { user, loading } = useAuth();
  // BTC3 Phase 3 — hooks first, before any early return (rules-of-hooks).
  const { caps: _adminCaps, ready: _adminReady } = useCapabilities();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [total, setTotal] = useState(0);
  const [count, setCount] = useState(0);
  const [bySource, setBySource] = useState({});
  const [globalSources, setGlobalSources] = useState([]);
  const [busy, setBusy] = useState(false);

  const fetchRows = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (filter !== "all") params.set("status", filter);
      if (sourceFilter !== "all") params.set("source", sourceFilter);
      const { data } = await api.get(`/admin/donations?${params.toString()}`);
      setRows(data.items || []);
      setCount(data.count || 0);
      setTotal(data.total_paid_gbp || 0);
      setBySource(data.by_source || {});
    } catch (_e) {
      // 403 handled by parent redirect
    } finally {
      setBusy(false);
    }
  };

  const fetchGlobalSources = async () => {
    try {
      const { data } = await api.get("/admin/donations/sources");
      setGlobalSources(data.sources || []);
    } catch (_e) { /* noop */ }
  };

  useEffect(() => {
    // BTC3 Foundation Verification · Step 4 — capability-driven
    // fetch trigger (replaces `user?.role === "admin"`).
    if (_adminReady && _adminCaps.admin_donations) {
      fetchRows();
      fetchGlobalSources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_adminReady, _adminCaps.admin_donations, filter, sourceFilter]);

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("btc_access_token");
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/donations/export.csv`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "btc_donations.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) { /* noop */ }
  };

  if (loading) return null;
  if (!user || typeof user !== "object") return <Navigate to="/login" replace />;
  // BTC3 Phase 3 · capability-driven visibility.  Backend API rejects
  // non-admin callers; the frontend now consults the resolver's
  // `admin_donations` capability rather than an inline role string.
  if (_adminReady && !_adminCaps.admin_donations) return <Navigate to="/portal/dashboard" replace />;

  const topSourceTotal = globalSources.reduce((sum, s) => sum + (s.total_gbp || 0), 0) || 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#23803F]">ADMIN</p>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-[#0B132B] tracking-tight">
            Donations
          </h1>
        </div>
        <button
          onClick={exportCsv}
          data-testid="admin-donations-export"
          className="btc-btn-outline text-sm inline-flex"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="text-[11px] uppercase tracking-wider text-[#4B5563] font-semibold">Total Raised (paid)</div>
          <div className="text-3xl font-extrabold text-[#23803F] mt-1">£{total.toFixed(2)}</div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100">
          <div className="text-[11px] uppercase tracking-wider text-[#4B5563] font-semibold">Records</div>
          <div className="text-3xl font-extrabold text-[#0B132B] mt-1">{count}</div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-gray-100 col-span-2">
          <div className="text-[11px] uppercase tracking-wider text-[#4B5563] font-semibold">Gift Aid declared</div>
          <div className="text-3xl font-extrabold text-[#1A5FAB] mt-1">
            {rows.filter((r) => r.gift_aid?.declared).length}
          </div>
        </div>
      </div>

      {/* Source breakdown */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#1A5FAB]/10 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-[#1A5FAB]" />
            </div>
            <div>
              <h3 className="font-bold text-[#0B132B]">Where donations come from</h3>
              <p className="text-xs text-[#4B5563]">Silently attributed on every donation via campaign links and referrer.</p>
            </div>
          </div>
        </div>
        {globalSources.length === 0 ? (
          <div className="text-sm text-[#4B5563] py-6 text-center">No attribution data yet.</div>
        ) : (
          <div className="space-y-3">
            {globalSources.map((s) => {
              const pct = ((s.total_gbp || 0) / topSourceTotal) * 100;
              return (
                <div key={s.source} data-testid="admin-source-row" className="grid grid-cols-[130px_1fr_auto] items-center gap-4">
                  <button
                    onClick={() => setSourceFilter(sourceFilter === s.source ? "all" : s.source)}
                    className={`text-left text-sm font-semibold flex items-center gap-2 transition ${
                      sourceFilter === s.source ? "text-[#0B132B]" : "text-[#4B5563] hover:text-[#0B132B]"
                    }`}
                  >
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: sourceColor(s.source) }} />
                    {s.source}
                  </button>
                  <div className="relative h-6 rounded-full bg-[#F8F9FB] overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 rounded-full transition-all"
                      style={{ width: `${Math.max(2, pct)}%`, backgroundColor: sourceColor(s.source) }}
                    />
                  </div>
                  <div className="text-right text-sm whitespace-nowrap">
                    <span className="font-bold text-[#0B132B]">£{s.total_gbp.toFixed(2)}</span>
                    <span className="text-xs text-[#4B5563] ml-2">{s.paid_count} paid · {s.count} total</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-4 flex flex-wrap items-center gap-2 border-b border-gray-100">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="admin-donations-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchRows()}
              placeholder="Search by name, email, or reference"
              className="w-full pl-9 pr-4 py-2 rounded-full bg-[#F8F9FB] border border-gray-200 text-sm focus:outline-none focus:border-[#2D9B4E]"
            />
          </div>
          <button
            onClick={fetchRows}
            disabled={busy}
            className="px-4 py-2 rounded-full bg-[#0B132B] text-white text-sm font-semibold hover:bg-black transition"
          >
            {busy ? "Loading..." : "Search"}
          </button>
          <select
            data-testid="admin-donations-source-filter"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-2 rounded-full bg-[#F8F9FB] border border-gray-200 text-xs font-semibold uppercase text-[#0B132B]"
          >
            <option value="all">All sources</option>
            {KNOWN_SOURCES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <div className="flex gap-2 ml-auto">
            {["all", "paid", "unpaid"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase transition ${
                  filter === s
                    ? "bg-[#2D9B4E] text-white"
                    : "bg-[#F8F9FB] text-[#0B132B] hover:bg-gray-100"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-[#F8F9FB] text-left text-[11px] uppercase tracking-wider text-[#4B5563]">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Donor</th>
                <th className="px-4 py-3">Reference</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Gift Aid</th>
                <th className="px-4 py-3">Payment</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-[#4B5563] py-16">
                    <Heart className="w-6 h-6 text-[#23803F] mx-auto mb-2" />
                    No donations yet.
                  </td>
                </tr>
              ) : (
                rows.map((d) => {
                  const s = statusPill(d.payment_status);
                  const src = d.source || "Direct";
                  return (
                    <tr key={d.id} className="border-t border-gray-100 hover:bg-[#F8F9FB]/40">
                      <td className="px-4 py-3 text-[#4B5563] whitespace-nowrap">
                        {new Date(d.created_at).toLocaleString("en-GB")}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-[#0B132B]">{d.donor_name}</div>
                        <div className="text-xs text-[#4B5563]">{d.donor_email}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-[#0B132B]">{d.reference}</td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full"
                          style={{
                            backgroundColor: `${sourceColor(src)}15`,
                            color: sourceColor(src),
                          }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: sourceColor(src) }} />
                          {src}
                        </span>
                        {d.utm?.campaign && (
                          <div className="text-[10px] text-[#4B5563] mt-1 font-mono truncate max-w-[140px]" title={d.utm.campaign}>
                            {d.utm.campaign}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-[#0B132B]">
                        £{Number(d.amount).toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        {d.gift_aid?.declared ? (
                          <span className="text-[10px] bg-[#1A5FAB]/10 text-[#1A5FAB] px-2 py-1 rounded-full font-bold">
                            DECLARED
                          </span>
                        ) : (
                          <span className="text-[10px] text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-[10px] px-2 py-1 rounded-full font-bold border inline-flex items-center gap-1 ${s.cls}`}
                        >
                          <s.Icon className="w-3 h-3" /> {s.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
